import React, { useCallback, useMemo } from 'react'
import { ClearOutlined } from '@ant-design/icons'
import { Button, Col, Row, Tooltip } from 'antd'
import { capitalize, filter, isNil } from 'lodash'
import PropTypes from 'prop-types'

import {
  AiHighlightDecoratedComponent,
  RichTextHighlightingInput,
} from '../../../common/components/sections'
import { handleAiHighlightStrategy } from '../../../common/helpers'
import {
  clearMiscAndChoiceFromText,
  containsMiscOrChoiceText,
  stripHtmlFromText,
} from '../../../common/utils'

import { useUpdateMenuTitleMutation } from '../../hooks'
import { StyledMenuTitleDescriptionContainer } from '../styled'

const MenuTitleEditableDescriptionCell = ({
  menuTitleId,
  fieldName,
  value,
  styledValue,
  isDisabled,
  menuTitleHighlights,
  highlightsFieldName,
  isTextEditingEnabled,
}) => {
  const [update] = useUpdateMenuTitleMutation(menuTitleId)

  const fieldHighlights = useMemo(
    () =>
      filter(
        menuTitleHighlights?.[menuTitleId],
        ({ originField }) => originField === highlightsFieldName,
      ),
    [menuTitleHighlights, menuTitleId, highlightsFieldName],
  )

  const hasMiscAndChoice = useMemo(
    () => containsMiscOrChoiceText(styledValue),
    [styledValue],
  )

  const currentValue = useMemo(
    () => styledValue || value || '',
    [styledValue, value],
  )

  const styledFieldName = useMemo(
    () => `styled${capitalize(fieldName)}`,
    [fieldName],
  )

  const handleChange = useCallback(
    newValue => {
      if (!isNil(newValue) && newValue !== currentValue) {
        update(menuTitleId, {
          [styledFieldName]: newValue,
          ...(isTextEditingEnabled && {
            [fieldName]: stripHtmlFromText(newValue),
          }),
        })
      }
    },
    [
      currentValue,
      update,
      menuTitleId,
      styledFieldName,
      isTextEditingEnabled,
      fieldName,
    ],
  )

  const handleClearMiscAndChoiceText = useCallback(() => {
    handleChange(clearMiscAndChoiceFromText(currentValue))
  }, [currentValue, handleChange])

  return (
    <Row gutter={16} align="middle">
      {hasMiscAndChoice && (
        <Col span={2}>
          <Tooltip title="Clear misc&choice text">
            <Button
              shape="circle"
              onClick={handleClearMiscAndChoiceText}
              icon={<ClearOutlined />}
            />
          </Tooltip>
        </Col>
      )}
      <StyledMenuTitleDescriptionContainer span={22}>
        <RichTextHighlightingInput
          value={currentValue}
          onChange={handleChange}
          onlySelection={!isTextEditingEnabled}
          isDisabled={isDisabled}
          toolbarItems={['italic', 'underline']}
          highlights={fieldHighlights}
          highlightStrategy={handleAiHighlightStrategy}
          decoratorComponent={AiHighlightDecoratedComponent}
        />
      </StyledMenuTitleDescriptionContainer>
    </Row>
  )
}

MenuTitleEditableDescriptionCell.propTypes = {
  fieldName: PropTypes.oneOf(['title', 'description']).isRequired,
  menuTitleId: PropTypes.string.isRequired,
  value: PropTypes.string,
  styledValue: PropTypes.string,
  menuTitleHighlights: PropTypes.object,
  highlightsFieldName: PropTypes.oneOf(['name', 'description']).isRequired,
  isDisabled: PropTypes.bool,
  isTextEditingEnabled: PropTypes.bool,
}

MenuTitleEditableDescriptionCell.defaultProps = {
  value: undefined,
  styledValue: undefined,
  isDisabled: false,
  menuTitleHighlights: [],
  isTextEditingEnabled: false,
}

export default MenuTitleEditableDescriptionCell
